/* eslint-disable default-param-last */
import React, { useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';

import {
  INVALID_ERROR,
  BLOCKED_ERROR,
  FORBIDDEN_ERROR,
  SESSION_EXPIRED_ERROR,
} from '@/assets/AppConstants';

import BlockScreen from '@/views/common/BlockScreen';
import AlertMessage from '@/views/common/AlertMessage';

import AppContext from './context';

const initialAlertProps = { autoHideDuration: 3000 };

const DEFAULT_APP_LOADING = {
  page: false,
  table: false,
  modal: false,
  blockScreen: false,
  invisibleBlockScreen: false,
};

const AppContextProvider = ({ children }) => {
  const [alertTitle, setAlertTitle] = useState('');
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState('');

  const [requestStatus, setRequestStatus] = useState(0);
  const [alertProps, setAlertProps] = useState(initialAlertProps);

  const [blockTable, setBlockTable] = useState(false);
  const [loadingBlockScreen, setLoadingBlockScreen] = useState(false);

  const handleRequestStatus = (status) => setRequestStatus(status);
  const handleResetStatus = () => setRequestStatus(0);
  const hasRequestStatus = () => requestStatus !== 0;

  const [loading, setLoading] = useState(DEFAULT_APP_LOADING);

  const setAppLoading = useCallback((value = { }) => {
    if (typeof value === 'boolean') {
      setLoading(() => ({
        page: value,
        table: value,
        modal: value,
        blockScreen: value,
        invisibleBlockScreen: value,
      }));

      return;
    }

    setLoading((prev) => ({ ...prev, ...value }));
  }, [setLoading]);

  const closeAlert = () => {
    setAlertMessage('');
    setAlertTitle('');
    setAlertSeverity('');
    setAlertProps(initialAlertProps);
  };

  const openAlert = async (message, severity, title = '', openAlertProps) => {
    if (React.isValidElement(message)) {
      setAlertMessage(message);
    } else if (typeof (message) === 'string') {
      setAlertMessage(message);
    } else {
      const { status } = message.response;
      let data = {};
      if (message.response?.data instanceof Blob) {
        data = JSON.parse(await message.response.data.text());
      } else {
        data = message.response.data;
      }
      if ([FORBIDDEN_ERROR, SESSION_EXPIRED_ERROR, BLOCKED_ERROR, INVALID_ERROR].includes(status)) {
        handleRequestStatus(status);
      } else {
        setAlertMessage(data);
      }
    }
    setAlertSeverity(severity);
    setAlertTitle(title);

    if (openAlertProps) setAlertProps(openAlertProps);
  };

  const handleBlockScreen = (blocked, screen = false) => {
    setLoadingBlockScreen(() => blocked);
    setTimeout(() => {
      setBlockTable(() => screen);
    }, !screen ? 1000 : 0);
  };

  const appValue = useMemo(() => ({
    hasBlockScreen: loadingBlockScreen,
    hasBlockTable: blockTable,
    blockScreen: handleBlockScreen,
    handleAlert: openAlert,

    isLoading: loading,
    setAppLoading,

    requestStatus,
    hasRequestStatus,
    handleResetStatus,
    handleRequestStatus,
  }), [
    loadingBlockScreen,
    blockTable,
    requestStatus,
    loading,
    setAppLoading,
  ]);

  const openBlockScreen = loading.blockScreen || loadingBlockScreen;
  const openInvisibleBlockScreen = loading.page || loading.invisibleBlockScreen;

  return (
    <AppContext.Provider value={appValue}>
      {children}
      {
        openBlockScreen && (
          <BlockScreen openBlockScreen={openBlockScreen} />
        )
      }
      {
        openInvisibleBlockScreen && (
          <BlockScreen
            openBlockScreen={openInvisibleBlockScreen}
            BackdropProps={{ sx: { opacity: '0 !important' } }}
          />
        )
      }
      {
        (alertMessage !== '' && alertSeverity !== '') && (
          <AlertMessage
            alertTitle={alertTitle}
            alertMessage={alertMessage}
            alertSeverity={alertSeverity}
            openAlert={(alertMessage !== '')}
            closeAlert={closeAlert}
            snackbarProps={alertProps}
          />
        )
      }
    </AppContext.Provider>
  );
};

AppContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default AppContextProvider;
